import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import type { Camera } from '@api/types';

export interface CameraCardContextMenuProps {
  camera: Camera | null;
  position: { x: number; y: number } | null;
  onClose: () => void;
  onSettings: (camera: Camera) => void;
  onDelete: (camera: Camera) => void;
}

export function CameraCardContextMenu({
  camera,
  position,
  onClose,
  onSettings,
  onDelete,
}: CameraCardContextMenuProps): JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!camera || !position) return;
    const onMouseDown = (e: MouseEvent): void => {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [camera, position, onClose]);

  if (!camera || !position) return null;

  const itemClass = clsx(
    'flex w-full items-center px-3 py-2 text-left text-sm text-foreground',
    'hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none',
  );

  const run = (fn: (c: Camera) => void): void => {
    fn(camera);
    onClose();
  };

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={`Actions for ${camera.name}`}
      className={clsx(
        'fixed z-[100] min-w-[11rem] rounded-md border border-border bg-surface py-1 shadow-card',
      )}
      style={{ left: position.x, top: position.y }}
    >
      <button
        type="button"
        role="menuitem"
        className={itemClass}
        onClick={() => run(onSettings)}
      >
        Settings…
      </button>
      <button
        type="button"
        role="menuitem"
        className={clsx(itemClass, 'text-danger hover:bg-danger/10')}
        onClick={() => run(onDelete)}
      >
        Delete camera…
      </button>
    </div>,
    document.body,
  );
}
